/*
 * KR Maps Journeys — "Try it yourself": a sample trip on a phone, on the
 * Journeys page (sections/kr-journeys-play.liquid), to scroll and to play as
 * in the app.
 *
 * The phone opens on the trip's journey page. Its photos scroll, and the map
 * over them follows as the app's does. Its Play opens the trip's Play screen,
 * which plays the trip back on the clock. Both maps are live: Mapbox GL JS
 * drawing the brand's street style, the same style the app shows, with the
 * line, its dots and the app's controls drawn over it here. Nothing is a
 * video.
 *
 * What the maps do is the app's own, recorded from the app
 * (docs/journeys-play-film):
 *   - The page (kr-page-<trip>.json): the list is the app's, captured as it
 *     lays it out (kr-page-<trip>-NN.webp) and scrolled by the browser. A
 *     trace of the page gives the camera for every point of scroll, as its
 *     flights between holds, and how far the line is drawn. The camera follows
 *     the reading line as the app's TrackFollower does: it holds still on a
 *     place while its photos go by, flies on to the next at a pace the eye can
 *     follow however fast the photos are flicked, and flies straight across a
 *     jump too far to travel.
 *   - Play (kr-play-<trip>.json): a trace of the app's Play screen, frame by
 *     frame: the camera, the line, the days dimming, the daylight over the
 *     map, the ribbon, the photos coming up (kr-play-<trip>-NN.webp) and every
 *     word on screen. It runs on its own clock, and plays, pauses and seeks as
 *     the app's does. It opens at the photo being read on the page, and
 *     closing it takes the page to the photo it stopped on.
 *
 * Markup: [data-kr-demo] (the phone) with data-token (a Mapbox public token)
 * and data-style, holding .kr-pg[data-page] and .kr-rp[data-play]; see the
 * section. Everything is placed from the recordings' own measurements, in the
 * app's points (1pt = --u).
 *
 * The How it works section (sections/kr-journeys-how.liquid) has the same
 * phone at the end of its story, held ([data-held], hold(true)) while the
 * page's own scroll drives the list (drive(s): the camera keeps to the trace
 * at each point), then let go (hold(false)) to be scrolled and played as here.
 * A copy that opens from a file carries the recordings in script elements
 * (data-page-inline, data-play-inline name them) in place of fetching them.
 */
(function () {
  'use strict';

  var GL_VERSION = '3.31.0';

  // The app's TrackFollower and journey page (lib/core/journey_camera.dart,
  // lib/screens/journey_screen.dart).
  var MAX_SPEED = 2;
  var ACCELERATION = 4;
  var BRAKING = 0.45 * ACCELERATION;
  var GAIN = 12;
  var FOLLOW = 15;
  var SNAP = 1800;

  var PAPER = [251, 250, 247];
  var HEAD = '#C2451A';
  var ACCENT = '#8A4A30';
  var MUTED = '#7C7566';
  var FAINT = '#9B9585';
  var HAIRLINE = 'rgba(33,29,22,0.2)';

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }

  function smoothstep(t) {
    var x = clamp(t, 0, 1);
    return x * x * (3 - 2 * x);
  }

  function px(n) {
    return 'calc(var(--u) * ' + n + ')';
  }

  function place(el, rect) {
    if (!el || !rect) return;
    el.style.left = px(rect[0]);
    el.style.top = px(rect[1]);
    if (rect.length > 2) {
      el.style.width = px(rect[2]);
      el.style.height = px(rect[3]);
    }
  }

  function mercatorX(lon) {
    return (lon + 180) / 360;
  }

  function mercatorY(lat) {
    var phi = clamp(lat, -85.0511287798, 85.0511287798) * Math.PI / 180;
    return 0.5 - Math.log(Math.tan(Math.PI / 4 + phi / 2)) / (2 * Math.PI);
  }

  function lngLat(shot) {
    return [shot[0] * 360 - 180, Math.atan(Math.sinh(Math.PI * (1 - 2 * shot[1]))) * 180 / Math.PI];
  }

  function rgba(hex, alpha) {
    var n = parseInt(hex.slice(1), 16);
    return 'rgba(' + (n >> 16 & 255) + ',' + (n >> 8 & 255) + ',' + (n & 255) + ',' + alpha + ')';
  }

  function event(name, detail) {
    try {
      return new CustomEvent(name, { detail: detail });
    } catch (e) {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, false, false, detail);
      return ev;
    }
  }

  /* ShotFlight: van Wijk and Nuij's zoom-and-pan path from shot a to shot b
     ([x, y, zoom], Mercator 0–1 and the app's zoom, 256 px tiles), at an even
     pace. */
  function flight(a, b, width) {
    var rho = 2, rho2 = 4;
    var w0 = width / (256 * Math.pow(2, a[2]));
    var w1 = width / (256 * Math.pow(2, b[2]));
    var dx = b[0] - a[0], dy = b[1] - a[1];
    var d2 = dx * dx + dy * dy;
    function zoom(w) {
      return Math.log(width / (256 * w)) / Math.LN2;
    }
    if (d2 < 1e-24) {
      var s0 = Math.log(w1 / w0) / rho;
      return {
        length: Math.abs(s0),
        at: function (t) {
          if (t <= 0) return a;
          if (t >= 1) return b;
          return [a[0], a[1], zoom(w0 * Math.exp(rho * t * s0))];
        }
      };
    }
    var d1 = Math.sqrt(d2);
    var b0 = (w1 * w1 - w0 * w0 + rho2 * rho2 * d2) / (2 * w0 * rho2 * d1);
    var b1 = (w1 * w1 - w0 * w0 - rho2 * rho2 * d2) / (2 * w1 * rho2 * d1);
    var r0 = -Math.asinh(b0), r1 = -Math.asinh(b1);
    var s = (r1 - r0) / rho;
    var coshR0 = Math.cosh(r0), sinhR0 = Math.sinh(r0);
    return {
      length: s,
      at: function (t) {
        if (t <= 0) return a;
        if (t >= 1) return b;
        var r = rho * t * s + r0;
        var u = w0 / (rho2 * d1) * (coshR0 * Math.tanh(r) - sinhR0);
        return [a[0] + u * dx, a[1] + u * dy, zoom(w0 * coshR0 / Math.cosh(r))];
      }
    };
  }

  /* [[s, v], …], s increasing: v at s, straight between, held past the ends.
     With `back`, the other way: s where v is reached (v increasing). */
  function lookup(table, s, back) {
    var i = back ? 1 : 0, o = back ? 0 : 1;
    var n = table.length;
    if (s <= table[0][i]) return table[0][o];
    if (s >= table[n - 1][i]) return table[n - 1][o];
    var lo = 0, hi = n - 1;
    while (hi - lo > 1) {
      var mid = (lo + hi) >> 1;
      if (table[mid][i] <= s) lo = mid;
      else hi = mid;
    }
    var a = table[lo], b = table[hi];
    return a[o] + (b[o] - a[o]) * (s - a[i]) / (b[i] - a[i] || 1);
  }

  /* ------------------------------------------------------------ the maps */

  var glLoading = null;

  /* Mapbox GL JS, once, from Mapbox's own CDN. */
  function loadGL() {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (glLoading) return glLoading;
    glLoading = new Promise(function (resolve, reject) {
      var base = 'https://api.mapbox.com/mapbox-gl-js/v' + GL_VERSION + '/mapbox-gl.';
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = base + 'css';
      document.head.appendChild(css);
      var script = document.createElement('script');
      script.src = base + 'js';
      script.async = true;
      script.onload = function () {
        if (window.mapboxgl) resolve(window.mapboxgl);
        else reject(new Error('no mapboxgl'));
      };
      script.onerror = function () {
        glLoading = null;
        reject(new Error('mapbox-gl did not load'));
      };
      document.head.appendChild(script);
    });
    return glLoading;
  }

  /* A live street map the size of the app's (W x H points), in `host`. The
     map is laid out at that size in CSS pixels and scaled down to the phone,
     so everything on it, labels and Mapbox's credit too, keeps the app's
     proportions. Cameras are the app's ([x, y, zoom], 256 px tiles); Mapbox
     counts zoom in 512 px ones. `paint` is called each time the map is drawn,
     with the camera it was drawn at, for what goes over it to keep with it. */
  function LiveMap(host, W, H, paint) {
    this.host = host;
    this.W = W;
    this.H = H;
    this.paint = paint;
    this.map = null;
    this.ready = false;
    this.failed = false;
    this.aimed = null;
    this.box = document.createElement('div');
    this.box.className = 'kr-gl';
    this.box.style.width = W + 'px';
    this.box.style.height = H + 'px';
    host.appendChild(this.box);
    this.fit();
  }

  LiveMap.prototype.scale = function () {
    return (this.host.clientWidth || this.W) / this.W;
  };

  LiveMap.prototype.fit = function () {
    var k = this.scale();
    this.box.style.transform = 'scale(' + k + ')';
    if (this.map && this.map.setPixelRatio) this.map.setPixelRatio(clamp((window.devicePixelRatio || 1) * k, 1, 3));
  };

  LiveMap.prototype.start = function (token, style, shot) {
    var self = this;
    return loadGL().then(function (gl) {
      if (!gl.supported || gl.supported()) {
        gl.accessToken = token;
        var map = self.map = new gl.Map({
          container: self.box,
          style: style,
          center: lngLat(shot),
          zoom: shot[2] - 1,
          interactive: false,
          projection: 'mercator',
          attributionControl: false,
          renderWorldCopies: false,
          antialias: false,
          minZoom: 0,
          maxZoom: 22,
          pixelRatio: clamp((window.devicePixelRatio || 1) * self.scale(), 1, 3),
          performanceMetricsCollection: false
        });
        map.addControl(new gl.AttributionControl({ compact: false }));
        self.aimed = shot;
        map.on('render', function () {
          var c = map.getCenter();
          self.paint([mercatorX(c.lng), mercatorY(c.lat), map.getZoom() + 1]);
        });
        map.once('load', function () {
          self.ready = true;
          self.host.classList.add('is-live');
          clearInterval(self.poke);
        });
        // Mapbox says "load" from a frame it draws; a map that has everything
        // in but has not been asked to draw since (out of sight, nothing
        // moving) gets asked.
        self.poke = setInterval(function () {
          if (self.ready || !self.map) return clearInterval(self.poke);
          if (map.loaded()) map.triggerRepaint();
        }, 500);
        map.on('error', function () { /* a tile that didn't come; the map carries on */ });
        return map;
      }
      throw new Error('no WebGL');
    }).catch(function (err) {
      self.failed = true;
      throw err;
    });
  };

  /* Puts the camera at `shot`. Moved, the map draws itself on the next
     frame, and what goes over it with it (MOVED); where it already was, only
     what goes over it needs drawing (STILL); with no map up, NONE. */
  var NONE = 0, MOVED = 1, STILL = 2;
  LiveMap.prototype.aim = function (shot) {
    if (!this.map) return NONE;
    var a = this.aimed;
    if (a && a[0] === shot[0] && a[1] === shot[1] && a[2] === shot[2]) return this.ready ? STILL : NONE;
    this.aimed = shot;
    this.map.jumpTo({ center: lngLat(shot), zoom: shot[2] - 1 });
    return this.ready ? MOVED : NONE;
  };

  LiveMap.prototype.idle = function (then, most) {
    var map = this.map, done = false;
    function go() {
      if (done) return;
      done = true;
      then();
    }
    if (!map) return go();
    map.once('idle', go);
    setTimeout(go, most);
  };

  LiveMap.prototype.remove = function () {
    clearInterval(this.poke);
    if (this.map) this.map.remove();
    this.map = null;
  };

  /* ------------------------------------------------------------ the line */

  /* The trip's line as JourneyMap draws it, over a map of W x H points at
     camera `shot`: a paper halo under a 3.5 pt core, each hop in its day's
     color, the dots at each place reached, where the journey set out, and
     where the line has got to. `p` is how far it's drawn (photo + fraction;
     null for all of it); `dims` how far each day has dimmed (Play's days gone
     by). flutter_map's own ways are kept: each see-through hop is its own
     stroke, and a hop into a new day spreads its two colors over its first
     half. */
  function Line(data) {
    var arc = data.line.arc;
    this.arc = arc;
    this.mx = new Float64Array(arc.length);
    this.my = new Float64Array(arc.length);
    for (var i = 0; i < arc.length; i++) {
      this.mx[i] = mercatorX(arc[i][1]);
      this.my[i] = mercatorY(arc[i][0]);
    }
    this.nodes = data.line.nodes.length;
    this.nodeVertex = data.line.nodeVertex;
    this.photoNode = data.line.photoNode;
    this.nodeDay = data.line.nodeDay;
    this.colors = data.dayColors;
  }

  Line.prototype.paint = function (ctx, W, H, shot, p, dims) {
    var self = this;
    var scale = 256 * Math.pow(2, shot[2]);
    var ox = W / 2 - shot[0] * scale, oy = H / 2 - shot[1] * scale;
    var mx = this.mx, my = this.my, n = mx.length, nv = this.nodeVertex;
    function X(i) { return mx[i] * scale + ox; }
    function Y(i) { return my[i] * scale + oy; }
    function dimOf(day) { return dims && day >= 0 && day < dims.length ? clamp(dims[day], 0, 1) : 0; }
    function color(node, alpha) { return rgba(self.colors[self.nodeDay[node]], alpha); }
    var photos = this.photoNode.length;
    var count = n, extra = null, reached = this.nodes - 1;
    if (p != null) {
      var i = clamp(Math.floor(p), 0, photos - 1);
      var fraction = p - Math.floor(p);
      var end = nv[this.photoNode[i]];
      count = end + 1;
      reached = this.photoNode[i];
      if (fraction > 0 && i + 1 < photos) {
        var next = nv[this.photoNode[i + 1]];
        if (next > end) {
          var reach = end + (next - end) * clamp(fraction, 0, 1);
          var whole = Math.floor(reach);
          count = whole + 1;
          var t = reach - whole;
          if (t > 0 && whole + 1 < n) {
            var a = this.arc[whole], b = this.arc[whole + 1];
            extra = [mercatorX(a[1] + (b[1] - a[1]) * t) * scale + ox, mercatorY(a[0] + (b[0] - a[0]) * t) * scale + oy];
          }
        }
      }
    }
    var drawn = count + (extra ? 1 : 0);
    function PX(v) { return v < count ? X(v) : extra[0]; }
    function PY(v) { return v < count ? Y(v) : extra[1]; }
    function path(from, to) {
      ctx.beginPath();
      ctx.moveTo(PX(from), PY(from));
      for (var v = from + 1; v <= to; v++) ctx.lineTo(PX(v), PY(v));
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    var hops = [];
    for (var h = 0; h < this.nodes - 1; h++) {
      var from = nv[h];
      if (from >= drawn - 1) break;
      var to = Math.min(nv[h + 1], drawn - 1);
      if (to - from < 1) continue;
      hops.push([h, from, to, dimOf(this.nodeDay[h + 1])]);
    }
    ctx.lineWidth = 6.5;
    for (var k = 0; k < hops.length; k++) {
      ctx.strokeStyle = 'rgba(' + PAPER.join(',') + ',' + (0.7 - 0.5 * hops[k][3]) + ')';
      path(hops[k][1], hops[k][2]);
      ctx.stroke();
    }
    ctx.lineWidth = 3.5;
    for (k = 0; k < hops.length; k++) {
      var hop = hops[k], alpha = 1 - 0.75 * hop[3];
      var d0 = this.nodeDay[hop[0]], d1 = this.nodeDay[hop[0] + 1];
      path(hop[1], hop[2]);
      if (d0 === d1) {
        ctx.strokeStyle = color(hop[0], alpha);
      } else {
        var g = ctx.createLinearGradient(PX(hop[1]), PY(hop[1]), PX(hop[2]), PY(hop[2]));
        g.addColorStop(0, color(hop[0], alpha));
        g.addColorStop(0.5, color(hop[0] + 1, alpha));
        g.addColorStop(1, color(hop[0] + 1, alpha));
        ctx.strokeStyle = g;
      }
      ctx.stroke();
    }
    for (var d = 1; d < this.nodes - 1 && d <= reached; d++) {
      var x = X(nv[d]), y = Y(nv[d]);
      if (x < -10 || y < -10 || x > W + 10 || y > H + 10) continue;
      var dotAlpha = 1 - 0.75 * dimOf(this.nodeDay[d]);
      ctx.fillStyle = color(d, dotAlpha);
      ctx.beginPath();
      ctx.arc(x, y, 4.5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,' + dotAlpha + ')';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(x, y, 3.9, 0, 2 * Math.PI);
      ctx.stroke();
    }
    if (n) {
      var sx = X(0), sy = Y(0);
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(sx, sy, 7, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sx, sy, 6, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fillStyle = ACCENT;
      ctx.beginPath();
      ctx.arc(sx, sy, 2, 0, 2 * Math.PI);
      ctx.fill();
    }
    if (drawn && (p != null || this.nodes > 1)) {
      var hx = PX(drawn - 1), hy = PY(drawn - 1);
      var glow = ctx.createRadialGradient(hx, hy, 0, hx, hy, 14);
      glow.addColorStop(0, 'rgba(194,69,26,0.4)');
      glow.addColorStop(0.45, 'rgba(194,69,26,0.3)');
      glow.addColorStop(1, 'rgba(194,69,26,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(hx, hy, 14, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = HEAD;
      ctx.beginPath();
      ctx.arc(hx, hy, 6.5, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(hx, hy, 5.5, 0, 2 * Math.PI);
      ctx.stroke();
    }
  };

  /* A canvas sized to its box at the screen's resolution, and the transform
     that draws in the app's points on it. */
  function fitCanvas(canvas, W) {
    var box = canvas.getBoundingClientRect();
    if (!box.width) return 0;
    var dpr = Math.min(2.5, window.devicePixelRatio || 1);
    var w = Math.round(box.width * dpr), h = Math.round(box.height * dpr);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    var k = w / W;
    var ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(k, 0, 0, k, 0, 0);
    return ctx;
  }

  /* The days at the seam under the map, each in its color, filled as far as
     `fill(d)` says (DayRibbon). */
  function paintRibbon(canvas, W, colors, fill) {
    var ctx = fitCanvas(canvas, W);
    if (!ctx) return;
    var days = colors.length;
    ctx.fillStyle = 'rgb(' + PAPER.join(',') + ')';
    ctx.fillRect(0, 0, W, 4);
    var each = (W - 2 * (days - 1)) / days;
    for (var d = 0; d < days; d++) {
      var x = d * (each + 2);
      ctx.fillStyle = rgba(colors[d], 0.26);
      ctx.fillRect(x, 0, each, 4);
      ctx.fillStyle = colors[d];
      ctx.fillRect(x, 0, each * clamp(fill(d), 0, 1), 4);
    }
  }

  /* ------------------------------------------------------------ the page's camera */

  /* The app's TrackFollower, with the page measured in scroll offset (points):
     the recording traced where the camera is at every point of scroll, so
     each leg carries how far along its flight the camera is at each offset. */
  function Follower(track, position) {
    this.track = track;
    this.jumpTo(position);
  }

  Follower.prototype.alongAt = function (leg, s) {
    if (s <= leg.s0) return 0;
    if (s >= leg.s1) return 1;
    return lookup(leg.along, s);
  };

  Follower.prototype.positionAt = function (leg, along) {
    if (along <= 0) return leg.s0;
    if (along >= 1) return leg.s1;
    return lookup(leg.along, along, true);
  };

  Follower.prototype.lastStartingBy = function (s) {
    var legs = this.track.legs, lo = 0, hi = legs.length;
    while (lo < hi) {
      var mid = (lo + hi) >> 1;
      if (legs[mid].s0 <= s) lo = mid + 1;
      else hi = mid;
    }
    return lo - 1;
  };

  Follower.prototype.trackAt = function (s) {
    var k = this.lastStartingBy(s);
    if (k < 0) return this.track.first;
    var leg = this.track.legs[k];
    if (s >= leg.s1) return leg.to;
    return leg.flight.at(this.alongAt(leg, s));
  };

  Follower.prototype.jumpTo = function (target) {
    this.loose = false;
    this.position = target;
    this.speed = 0;
    this.pageSpeed = 0;
    var k = this.lastStartingBy(target);
    var leg = k < 0 ? null : this.track.legs[k];
    if (leg && target < leg.s1) {
      this.leg = k;
      this.moving = true;
      this.along = this.alongAt(leg, target);
    } else {
      this.leg = k + 1;
      this.moving = false;
      this.along = 0;
    }
  };

  Follower.prototype.shot = function () {
    if (this.loose) return this.trackAt(this.position);
    if (this.moving) return this.track.legs[this.leg].flight.at(this.along);
    return this.leg === 0 ? this.track.first : this.track.legs[this.leg - 1].to;
  };

  Follower.prototype.settledAt = function (target) {
    return !this.moving && Math.abs(this.position - target) < 0.5 && Math.abs(this.pageSpeed) < 5;
  };

  /* Nothing moving: settled, or stopped partway along a leg where the page
     stopped (where the app's ticker runs on, doing nothing). */
  Follower.prototype.restingAt = function (target) {
    if (!this.moving) return this.settledAt(target);
    var leg = this.track.legs[this.leg];
    return Math.abs(this.speed) < 1e-3 && Math.abs(this.alongAt(leg, target) - this.along) * leg.length < 1e-4;
  };

  Follower.prototype.spring = function (target, dt) {
    var x = this.position - target;
    var decay = Math.exp(-FOLLOW * dt);
    var c = this.pageSpeed + FOLLOW * x;
    this.position = target + (x + c * dt) * decay;
    this.pageSpeed = (this.pageSpeed - FOLLOW * c * dt) * decay;
  };

  Follower.prototype.keepLoose = function (target, dt) {
    if (dt <= 0) return;
    this.loose = true;
    this.moving = false;
    this.speed = 0;
    this.spring(target, dt);
    if (Math.abs(this.position - target) < 0.5 && Math.abs(this.pageSpeed) < 5) {
      this.position = target;
      this.pageSpeed = 0;
    }
  };

  Follower.prototype.set = function (leg, along, at) {
    this.leg = leg;
    this.moving = true;
    this.along = along;
    this.speed = 0;
    this.pageSpeed = 0;
    this.position = at;
  };

  Follower.prototype.step = function (target, dt) {
    if (dt <= 0) return;
    if (this.loose) this.jumpTo(this.position);
    var legs = this.track.legs;
    if (!this.moving) {
      var before = this.leg > 0 ? legs[this.leg - 1].s1 : -Infinity;
      var after = this.leg < legs.length ? legs[this.leg].s0 : Infinity;
      this.spring(target, dt);
      if (this.position >= after && target > after) {
        this.set(this.leg, 0, after);
      } else if (this.position <= before && target < before) {
        this.set(this.leg - 1, 1, before);
      } else {
        if (this.position < before || this.position > after) this.pageSpeed = 0;
        this.position = clamp(this.position, before, after);
        if (Math.abs(this.position - target) < 0.5 && Math.abs(this.pageSpeed) < 5) {
          this.position = clamp(target, before, after);
          this.pageSpeed = 0;
        }
        return;
      }
    }
    var leg = legs[this.leg];
    var want = this.alongAt(leg, target);
    var length = leg.length;
    if (length < 1e-6) {
      this.along = want;
      this.speed = 0;
    } else {
      var gap = (want - this.along) * length;
      var reach = Math.min(MAX_SPEED, Math.min(Math.sqrt(2 * BRAKING * Math.abs(gap)), GAIN * Math.abs(gap)));
      this.speed += clamp((gap < 0 ? -1 : gap > 0 ? 1 : 0) * reach - this.speed, -ACCELERATION * dt, ACCELERATION * dt);
      this.along += this.speed * dt / length;
      if (this.along <= 0 || this.along >= 1) {
        this.along = clamp(this.along, 0, 1);
        this.speed = 0;
      }
    }
    this.position = this.positionAt(leg, this.along);
    var near = 0.004 / Math.max(length, 1e-6);
    if (want === 1 && this.along >= 1 - near && Math.abs(this.speed) < 0.1) {
      this.moving = false;
      this.leg += 1;
      this.position = leg.s1;
      this.speed = 0;
    } else if (want === 0 && this.along <= near && Math.abs(this.speed) < 0.1) {
      this.moving = false;
      this.position = leg.s0;
      this.speed = 0;
    }
  };

  /* ------------------------------------------------------------ the page */

  function Page(el, demo) {
    this.el = el;
    this.demo = demo;
    this.src = new URL(el.getAttribute('data-page'), location.href).href;
    this.mapHost = el.querySelector('.kr-pg__map');
    this.overlay = el.querySelector('.kr-pg__line');
    this.ribbon = el.querySelector('.kr-pg__ribbon');
    this.sheet = el.querySelector('.kr-pg__sheet');
    this.list = el.querySelector('.kr-pg__list');
    this.head = el.querySelector('.kr-pg__head');
    this.headTop = el.querySelector('.kr-pg__head-top');
    this.headLine = el.querySelector('.kr-pg__head-line');
    this.playBtn = el.querySelector('.kr-pg__play');
    this.wholeBtn = el.querySelector('.kr-pg__whole');
    this.startBtn = el.querySelector('.kr-pg__start');
    this.recenterBtn = el.querySelector('.kr-pg__recenter');
    this.hint = el.querySelector('.kr-pg__hint');
    this.data = null;
    this.live = false;
    this.overview = false;
    this.atTop = true;
    this.readingDay = -2;
    this.headPhoto = -1;
    this.now = 0;
    this.last = 0;
    this.loop = 0;
    this.manual = false;
    this.flightFrom = null;
    this.flightStart = 0;
    this.flightTime = 0;
    this.camera = null;
    this.lastCamera = null;
    this.cameraSpeed = 0;
    this.progress = null;
    this.map = null;
    this.driven = null;        // the scroll offset it is put at from outside, or null
    this.driveWanted = null;
  }

  /* The recording's JSON: fetched, or read from a script element the page
     carries (data-*-inline names it), for a copy that opens from a file. */
  function loadJSON(src, inlineId, what) {
    var inline = inlineId && document.getElementById(inlineId);
    if (inline) {
      try {
        return Promise.resolve(JSON.parse(inline.textContent));
      } catch (e) {
        return Promise.reject(new Error(what + ' inline: ' + e.message));
      }
    }
    return fetch(src, { credentials: 'omit' }).then(function (r) {
      if (!r.ok) throw new Error(what + ' ' + r.status);
      return r.json();
    });
  }

  Page.prototype.load = function () {
    var self = this;
    return loadJSON(this.src, this.el.getAttribute('data-page-inline'), 'page').then(function (data) {
      self.start(data);
    });
  };

  Page.prototype.start = function (data) {
    var self = this, L = data.layout;
    this.data = data;
    this.W = L.map[2];
    this.H = L.map[3];
    this.lineArt = new Line(data);
    var legs = [];
    for (var k = 0; k < data.legs.length; k++) {
      var lg = data.legs[k];
      legs.push({ s0: lg.s[0], s1: lg.s[1], from: lg.from, to: lg.to, length: lg.length, along: lg.along, flight: flight(lg.from, lg.to, this.W) });
    }
    this.track = { first: data.first, legs: legs };
    this.follower = new Follower(this.track, 0);
    this.camera = this.track.first;
    this.map = new LiveMap(this.mapHost, this.W, this.H, function (shot) { self.paint(shot); });

    // The controls over the map: Play at the right, Recenter at the left,
    // above the credit; the rest across the top.
    place(this.head, L.head);
    this.head.style.width = 'auto';
    this.startBtn.style.right = px(this.W - L.pill[0] - L.pill[2]);
    this.startBtn.style.top = px(L.pill[1]);
    if (this.recenterBtn) {
      this.recenterBtn.style.left = px(12);
      this.recenterBtn.style.top = px(L.pill[1]);
    }
    place(this.el.querySelector('.kr-pg__back'), L.back);
    place(this.el.querySelector('.kr-pg__more'), L.more);
    place(this.ribbon, L.ribbon);
    place(this.sheet, L.list);
    this.buildList();
    this.controls();
    this.el.classList.add('is-ready');

    this.sheet.addEventListener('scroll', function () { self.kick(); }, { passive: true });
    this.sheet.addEventListener('click', function () { self.tapped(); });
    this.sheet.addEventListener('focus', function () { self.goLive(); });
    // With a mouse or a trackpad, the list scrolls straight away, as it
    // would on the phone; on a touch screen it waits for a tap (below).
    this.sheet.addEventListener('wheel', function () { if (self.demo.fine) self.goLive(); }, { passive: true });
    if (this.hint) this.hint.addEventListener('click', function () { self.tapped(); });
    if (this.playBtn) this.playBtn.addEventListener('click', function () { self.play(); });
    if (this.startBtn) this.startBtn.addEventListener('click', function () { self.play(); });
    if (this.wholeBtn) this.wholeBtn.addEventListener('click', function () { self.showWhole(); });
    if (this.recenterBtn) this.recenterBtn.addEventListener('click', function () { self.recenter(); });
    if (this.demo.fine) this.goLive();
    if (this.wanted) this.wake();
    if (this.driveWanted != null) this.drive(this.driveWanted);
    this.kick();
  };

  /* Driven from outside (the How it works stage, by the page's own scroll):
     the list is put at `s` points of scroll, and the camera keeps to the
     trace at that point, the app's camera caught up, so the way back is the
     way forward and nothing moves once the scroll has stopped. */
  Page.prototype.drive = function (s) {
    if (!this.data) {
      this.driveWanted = s;
      return;
    }
    this.driveWanted = null;
    if (this.driven === s) return;
    this.driven = s;
    this.scrollTo(s);
    this.dirty = true;
    this.kick();
  };

  /* Given back to its own scrolling, from where it stands. */
  Page.prototype.release = function () {
    this.driveWanted = null;
    if (this.driven == null) return;
    this.driven = null;
    this.dirty = true;
    this.kick();
  };

  /* The map, once the demo is near: Mapbox's script and style load then. Near
     before the page's own data is in (a link straight to the demo), it waits
     for the data. */
  Page.prototype.wake = function () {
    var self = this;
    if (this.woken) return;
    if (!this.data) {
      this.wanted = true;
      return;
    }
    this.woken = true;
    this.map.start(this.demo.token, this.demo.style, this.camera).then(function () {
      self.dirty = true;
      self.kick();
    }, function () {
      self.el.classList.add('is-mapless');
      self.kick();
    });
  };

  Page.prototype.scrollOffset = function () {
    var room = this.sheet.scrollHeight - this.sheet.clientHeight;
    if (!this.data || room <= 0) return 0;
    return clamp(this.sheet.scrollTop / room, 0, 1) * this.data.maxScroll;
  };

  Page.prototype.scrollTo = function (s) {
    var room = this.sheet.scrollHeight - this.sheet.clientHeight;
    if (room > 0) this.sheet.scrollTop = clamp(s / this.data.maxScroll, 0, 1) * room;
  };

  /* How far the line is drawn: photo + fraction, or null up among the title,
     where the whole line shows. */
  Page.prototype.progressAt = function (s) {
    if (this.atTop) return null;
    var p = lookup(this.data.progress, s);
    return p < 0 ? null : p;
  };

  Page.prototype.dayOf = function (photo) {
    var starts = this.data.dayStarts, d = 0;
    while (d + 1 < starts.length && starts[d + 1] <= photo) d++;
    return d;
  };

  Page.prototype.following = function () {
    return !this.overview;
  };

  Page.prototype.aim = function () {
    return this.overview ? this.track.first : this.follower.shot();
  };

  /* Flies the camera from where it is onto what it should show, taking longer
     the further it has to go. */
  Page.prototype.takeOff = function () {
    if (!this.camera) return;
    var from = this.camera;
    this.flightFrom = from;
    this.flightStart = this.now;
    this.flightTime = clamp(0.35 + 1.5 * flight(from, this.aim(), this.W).length / MAX_SPEED, 0.4, 2.6);
    this.kick();
  };

  Page.prototype.tick = function (dt) {
    var s = this.driven != null ? this.driven : this.scrollOffset();
    var f = this.follower;
    this.atTop = s < this.data.atTop;
    var target = this.atTop ? 0 : s;
    if (this.driven != null) {
      // Where the trace has the camera at this point of scroll, at once.
      f.loose = true;
      f.moving = false;
      f.position = target;
      f.speed = 0;
      f.pageSpeed = 0;
      this.flightFrom = null;
    } else if (!this.following()) {
      f.keepLoose(target, dt);
    } else {
      var behind = Math.abs(target - f.position);
      if (behind > SNAP && (!f.moving || behind > 3 * SNAP)) {
        f.jumpTo(target);
        this.takeOff();
      } else {
        for (var left = dt; left > 1e-9; left -= 1 / 60) f.step(target, Math.min(left, 1 / 60));
      }
    }
    var p = this.progressAt(f.position);
    var day = p == null ? -1 : this.dayOf(Math.floor(p));
    if (day !== this.readingDay) {
      if (day < 0) this.overview = false;
      this.readingDay = day;
      this.controls();
    }
    if (p != null) {
      var photo = clamp(Math.floor(p), 0, this.data.heads.length - 1);
      if (photo !== this.headPhoto) {
        this.headPhoto = photo;
        this.headTop.textContent = this.data.heads[photo][0];
        this.headLine.textContent = this.data.heads[photo][1];
      }
    }
    var shot = this.aim();
    if (this.flightFrom) {
      var t = (this.now - this.flightStart) / this.flightTime;
      if (t >= 1) this.flightFrom = null;
      else shot = flight(this.flightFrom, shot, this.W).at(smoothstep(t));
    }
    if (dt > 0) {
      var moved = this.lastCamera ? flight(this.lastCamera, shot, this.W).length / dt : 0;
      this.cameraSpeed = this.cameraSpeed * 0.6 + moved * 0.4;
      this.lastCamera = shot;
    }
    this.camera = shot;
    this.progress = p;
    paintRibbon(this.ribbon, this.W, this.data.dayColors, this.fillOf(p));
    // The line goes on with the map, when the map draws. Until there is one
    // (or if there can't be), over the poster: the whole journey.
    var aimed = this.map.aim(shot);
    if (aimed === STILL) this.paint(shot);
    else if (aimed === NONE) this.paint(this.track.first);
    return !this.flightFrom && this.cameraSpeed < 0.01 && (this.following() ? f.restingAt(target) : true);
  };

  /* How much of each day has been read: all of every day up among the
     title, where the whole line shows. */
  Page.prototype.fillOf = function (p) {
    var starts = this.data.dayStarts, photos = this.data.heads.length;
    return function (d) {
      if (p == null) return 1;
      var first = starts[d], last = (d + 1 < starts.length ? starts[d + 1] : photos) - 1;
      return (p - first + 1) / (last - first + 1);
    };
  };

  Page.prototype.paint = function (shot) {
    var ctx = fitCanvas(this.overlay, this.W);
    if (!ctx || !this.data) return;
    this.lineArt.paint(ctx, this.W, this.H, shot, this.progress, null);
  };

  Page.prototype.frame = function (ms) {
    this.loop = 0;
    if (this.demo.dead || !this.data) return;
    var dt = this.last ? clamp((ms - this.last) / 1000, 0, 0.1) : 1 / 60;
    this.last = ms;
    this.now += dt;
    var still = this.tick(dt);
    if (!still || this.dirty) {
      this.dirty = false;
      this.loop = requestAnimationFrame(this.frameBound);
    } else {
      this.last = 0;
    }
  };

  Page.prototype.kick = function () {
    if (this.loop || this.demo.dead || !this.data || this.manual) return;
    if (!this.frameBound) this.frameBound = this.frame.bind(this);
    this.loop = requestAnimationFrame(this.frameBound);
  };

  Page.prototype.controls = function () {
    var reading = this.readingDay >= 0, L = this.data.layout;
    this.el.classList.toggle('is-reading', reading);
    this.el.classList.toggle('is-overview', this.overview);
    // Right to left: More, the whole journey (not while showing it), Play.
    var x = L.more[0];
    if (reading && !this.overview) {
      x -= 54;
      place(this.wholeBtn, [x, L.whole[1], 44, 44]);
    }
    if (reading) {
      x -= 54;
      place(this.playBtn, [x, L.play[1], 44, 44]);
    }
    this.head.style.maxWidth = px(x - 10 - L.head[0]);
  };

  Page.prototype.showWhole = function () {
    this.overview = true;
    this.controls();
    this.takeOff();
  };

  Page.prototype.recenter = function () {
    this.overview = false;
    this.controls();
    this.takeOff();
  };

  /* Play, from the photo being read (from the start up among the title). */
  Page.prototype.play = function () {
    var p = this.progressAt(this.follower.position);
    this.demo.openPlay(p == null ? 0 : clamp(Math.floor(p), 0, this.data.heads.length - 1));
  };

  /* Back from Play: the page picks up at the photo it stopped on, and the
     camera flies there. */
  Page.prototype.back = function (photo) {
    this.overview = false;
    this.goLive();
    this.scrollTo(this.data.photoS[clamp(photo, 0, this.data.photoS.length - 1)]);
    this.last = 0;
    this.kick();
  };

  Page.prototype.buildList = function () {
    var st = this.data.strips, k = this.W / st.width, u = 'var(--u)', self = this;
    this.list.style.height = 'calc(' + u + ' * ' + (this.data.maxScroll + this.data.viewport) + ')';
    var imgs = [];
    for (var i = 0; i < st.files.length; i++) {
      var f = st.files[i];
      var img = document.createElement('img');
      img.className = 'kr-pg__strip';
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      img.style.top = 'calc(' + u + ' * ' + (f[1] * k) + ')';
      img.style.height = 'calc(' + u + ' * ' + (f[3] * k) + ')';
      img.style.zIndex = String(i + 1);
      img.setAttribute('data-src', new URL(f[0], this.src).href);
      this.list.appendChild(img);
      imgs.push(img);
    }
    function load(el) {
      if (!el.getAttribute('src')) el.src = el.getAttribute('data-src');
    }
    load(imgs[0]);
    if (imgs[1]) load(imgs[1]);
    if (typeof IntersectionObserver === 'function') {
      var io = this.io = new IntersectionObserver(function (entries) {
        for (var j = 0; j < entries.length; j++) {
          if (entries[j].isIntersecting) {
            load(entries[j].target);
            io.unobserve(entries[j].target);
          }
        }
      }, { root: self.sheet, rootMargin: '1600px 0px' });
      for (var n = 2; n < imgs.length; n++) io.observe(imgs[n]);
    } else {
      for (var m = 2; m < imgs.length; m++) load(imgs[m]);
    }
  };

  /* The list scrolls once it's asked to on a touch screen (a tap on it), so
     a thumb scrolling the website past the phone goes on scrolling the
     website; out of sight, it asks again. With a mouse, straight away. */
  Page.prototype.goLive = function () {
    if (this.live || this.demo.held) return;
    this.live = true;
    this.el.classList.add('is-live');
  };

  Page.prototype.sleep = function () {
    if (this.demo.fine || !this.live) return;
    this.live = false;
    this.el.classList.remove('is-live');
  };

  Page.prototype.tapped = function () {
    if (this.live && !this.nudged) return;
    var first = !this.live;
    this.goLive();
    // A nudge down past the title to the first photo, so the map is seen to
    // follow: it flies in from the whole journey.
    if (first && this.scrollOffset() < 1) {
      var to = (this.sheet.scrollHeight - this.sheet.clientHeight) * clamp(this.data.nudge / this.data.maxScroll, 0, 1);
      try {
        this.sheet.scrollTo({ top: to, behavior: 'smooth' });
      } catch (e) {
        this.sheet.scrollTop = to;
      }
    }
  };

  Page.prototype.state = function () {
    return this.data && {
      scroll: this.scrollOffset(),
      position: this.follower.position,
      moving: this.follower.moving,
      camera: this.camera,
      progress: this.progressAt(this.follower.position),
      flight: !!this.flightFrom,
      overview: this.overview,
      live: this.live,
      map: !!(this.map && this.map.ready)
    };
  };

  /* For recording the demo frame by frame: the clock stops, and each call
     moves it on by dt seconds. */
  Page.prototype.step = function (dt) {
    this.manual = true;
    if (this.loop) cancelAnimationFrame(this.loop);
    this.loop = 0;
    this.now += dt;
    this.tick(dt);
  };

  /* ------------------------------------------------------------ Play */

  function Play(el, demo) {
    this.el = el;
    this.demo = demo;
    this.src = new URL(el.getAttribute('data-play'), location.href).href;
    this.mapHost = el.querySelector('.kr-rp__map');
    this.overlay = el.querySelector('.kr-rp__line');
    this.ribbon = el.querySelector('.kr-rp__ribbon');
    this.photosEl = el.querySelector('.kr-rp__photos');
    this.head = el.querySelector('.kr-rp__head');
    this.headTop = el.querySelector('.kr-rp__head-top');
    this.headLine = el.querySelector('.kr-rp__head-line');
    this.caption = el.querySelector('.kr-rp__caption');
    this.count = el.querySelector('.kr-rp__count');
    this.playBtn = el.querySelector('.kr-rp__play');
    this.statusEl = el.querySelector('.kr-rp__status');
    this.whenEl = el.querySelector('.kr-rp__when');
    this.rulerEl = el.querySelector('.kr-rp__ruler');
    this.data = null;
    this.t = 0;
    this.playing = false;
    this.waiting = false;
    this.loop = 0;
    this.last = 0;
    this.imgs = [];
    this.map = null;
    this.shown = { p: 0, dims: null, sky: null };
  }

  Play.prototype.load = function () {
    var self = this;
    if (this.loading) return this.loading;
    this.loading = loadJSON(this.src, this.el.getAttribute('data-play-inline'), 'play').then(function (data) {
      self.start(data);
    });
    return this.loading;
  };

  Play.prototype.start = function (data) {
    var self = this, L = data.layout;
    this.data = data;
    this.W = L.map[2];
    this.H = L.map[3];
    this.lineArt = new Line(this.demo.page.data);
    place(this.el.querySelector('.kr-rp__close'), L.close);
    place(this.head, [L.head[0], L.head[1]]);
    this.head.style.maxWidth = px(this.W - 10 - L.head[0]);
    place(this.ribbon, L.ribbon);
    place(this.photosEl, L.photoArea);
    place(this.el.querySelector('.kr-rp__captions'), [L.caption[0], L.caption[1], this.W - 2 * L.caption[0], L.caption[3]]);
    // The app's play button is a 44 pt square around its icon.
    place(this.playBtn, [L.play[0] - 11, L.play[1] - 11, 44, 44]);
    place(this.statusEl, [L.status[0], L.status[1]]);
    place(this.whenEl, [L.when[0], L.when[1]]);
    place(this.rulerEl, L.ruler);
    place(this.el.querySelector('.kr-rp__back'), [L.back[0] - 27, L.back[1] - 16, L.back[2] + 27, L.back[3] + 32]);
    for (var i = 0; i < data.photoFiles.length; i++) {
      var img = document.createElement('img');
      img.className = 'kr-rp__photo';
      img.alt = '';
      img.decoding = 'async';
      img.draggable = false;
      img.setAttribute('data-src', new URL(data.photoFiles[i], this.src).href);
      this.photosEl.appendChild(img);
      this.imgs.push(img);
    }
    this.map = new LiveMap(this.mapHost, this.W, this.H, function (shot) { self.paint(shot); });

    this.playBtn.addEventListener('click', function () { self.toggle(); });
    this.photosEl.addEventListener('click', function () { self.toggle(); });
    this.el.querySelector('.kr-rp__close').addEventListener('click', function () { self.demo.closePlay(); });
    this.el.querySelector('.kr-rp__back').addEventListener('click', function () { self.demo.closePlay(); });
    var r = this.rulerEl;
    r.addEventListener('pointerdown', function (e) {
      self.scrubbing = true;
      try { r.setPointerCapture(e.pointerId); } catch (err) { /* gone */ }
      self.seek(self.rulerAt(e));
      e.preventDefault();
    });
    r.addEventListener('pointermove', function (e) { if (self.scrubbing) self.seek(self.rulerAt(e)); });
    r.addEventListener('pointerup', function () { self.scrubbing = false; });
    r.addEventListener('pointercancel', function () { self.scrubbing = false; });
    r.addEventListener('keydown', function (e) {
      var step = self.data.duration / 40;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') self.seek(self.t + step);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') self.seek(self.t - step);
      else if (e.key === 'Home') self.seek(0);
      else if (e.key === 'End') self.seek(self.data.duration);
      else return;
      e.preventDefault();
    });
    this.el.classList.add('is-ready');
  };

  Play.prototype.rulerAt = function (e) {
    var box = this.rulerEl.getBoundingClientRect();
    return (e.clientX - box.left) / box.width * this.data.duration;
  };

  /* In from the page at photo `photo`, playing once its map has come in. */
  Play.prototype.open = function (photo) {
    var self = this;
    this.load().then(function () {
      var d = self.data;
      self.t = photo > 0 ? clamp(d.showing[photo], 0, d.duration) : 0;
      self.playing = false;
      self.waiting = true;
      self.render();
      var go = function () {
        if (!self.demo.filmOpen) return;
        self.waiting = false;
        self.play();
      };
      if (!self.map.map && !self.map.failed) {
        self.map.start(self.demo.token, self.demo.style, self.frameAt(self.t).camera).then(function () {
          self.map.idle(go, 2500);
        }, go);
      } else {
        self.map.idle(go, 600);
      }
    });
  };

  Play.prototype.close = function () {
    this.pause();
    this.waiting = false;
    var f = this.data ? clamp(Math.round(this.t * this.data.fps), 0, this.data.frames - 1) : 0;
    return this.data ? this.data.shownAt[f] : 0;
  };

  Play.prototype.toggle = function () {
    if (this.playing) this.pause();
    else this.play();
  };

  Play.prototype.play = function () {
    if (!this.data) return;
    if (this.t >= this.data.duration - 1e-3) this.t = 0;
    this.playing = true;
    this.last = 0;
    this.kick();
    this.render();
  };

  Play.prototype.pause = function () {
    this.playing = false;
    this.render();
  };

  /* Seeking pauses, as the app's scrubber does. */
  Play.prototype.seek = function (s) {
    if (!this.data) return;
    this.playing = false;
    this.waiting = false;
    this.t = clamp(s, 0, this.data.duration);
    this.render();
  };

  Play.prototype.kick = function () {
    if (this.loop || this.demo.dead) return;
    var self = this;
    this.loop = requestAnimationFrame(function tick(ms) {
      self.loop = 0;
      if (!self.playing || self.manual) return;
      var dt = self.last ? clamp((ms - self.last) / 1000, 0, 0.1) : 0;
      self.last = ms;
      self.t += dt;
      if (self.t >= self.data.duration) {
        self.t = self.data.duration;
        self.playing = false;
      }
      self.render();
      if (self.playing) self.loop = requestAnimationFrame(tick);
    });
  };

  /* Everything the app showed at `s` seconds: straight between its frames
     for what moves, the frame's own for words. */
  Play.prototype.frameAt = function (s) {
    var d = this.data, f = clamp(s * d.fps, 0, d.frames - 1);
    var i = Math.floor(f), j = Math.min(i + 1, d.frames - 1), a = f - i;
    function mix(x, y) { return x + (y - x) * a; }
    function mixAll(x, y) {
      var out = [];
      for (var k = 0; k < x.length; k++) out.push(mix(x[k], y[k]));
      return out;
    }
    var c0 = d.camera[i], c1 = d.camera[j];
    return {
      frame: i,
      camera: [mix(c0[0], c1[0]), mix(c0[1], c1[1]), mix(c0[2], c1[2])],
      line: mix(d.line[i], d.line[j]),
      dims: mixAll(d.dims[i], d.dims[j]),
      sky: mixAll(d.sky[i], d.sky[j]),
      ribbon: mixAll(d.ribbon[i], d.ribbon[j]),
      slides: this.slidesAt(i, j, a),
      words: d.words[i]
    };
  };

  Play.prototype.slidesAt = function (i, j, a) {
    var d = this.data, x = d.slides[i], y = d.slides[j];
    var out = [];
    for (var k = 0; k < x.length; k++) {
      var s = x[k].slice();
      for (var m = 0; m < y.length; m++) {
        if (y[m][0] === s[0]) {
          s[1] += (y[m][1] - s[1]) * a;
          s[2] += (y[m][2] - s[2]) * a;
        }
      }
      out.push(s);
    }
    return out;
  };

  Play.prototype.render = function () {
    if (!this.data) return;
    var d = this.data, fr = this.frameAt(this.t);
    var str = function (k) { return d.strings[fr.words[k]]; };
    this.shown = { p: fr.line, dims: fr.dims, sky: fr.sky };
    var aimed = this.map.aim(fr.camera);
    if (aimed === STILL || (aimed === NONE && this.map.failed)) this.paint(fr.camera);
    paintRibbon(this.ribbon, this.W, d.dayColors, function (day) { return fr.ribbon[day]; });
    // The photos up: the one fading in over the one before, each drifting
    // slowly closer.
    var up = {};
    for (var k = 0; k < fr.slides.length; k++) {
      var s = fr.slides[k], img = this.imgs[s[0]];
      up[s[0]] = true;
      if (!img.getAttribute('src')) img.src = img.getAttribute('data-src');
      img.style.zIndex = String(k + 1);
      img.style.opacity = String(s[1]);
      img.style.transformOrigin = ((s[3] + 1) * 50) + '% ' + ((s[4] + 1) * 50) + '%';
      img.style.transform = 'scale(' + s[2] + ')';
      img.style.visibility = 'visible';
    }
    var top = fr.slides.length ? fr.slides[fr.slides.length - 1][0] : 0;
    for (var n = 0; n < this.imgs.length; n++) {
      if (!up[n]) this.imgs[n].style.visibility = 'hidden';
      // The next ones load before they're needed, as the app's do.
      if (n > top && n <= top + 2 && !this.imgs[n].getAttribute('src')) this.imgs[n].src = this.imgs[n].getAttribute('data-src');
    }
    var headTop = str(0), headLine = str(1);
    if (this.headTop.textContent !== headTop) this.headTop.textContent = headTop;
    if (this.headLine.textContent !== headLine) this.headLine.textContent = headLine;
    if (this.caption.textContent !== str(2)) this.caption.textContent = str(2);
    if (this.count.textContent !== str(3)) this.count.textContent = str(3);
    if (this.whenEl.textContent !== str(4)) this.whenEl.textContent = str(4);
    var status = this.waiting ? 'Loading the trip' : str(5) || (this.playing ? 'Playing the trip' : 'Paused');
    if (this.statusEl.textContent !== status) this.statusEl.textContent = status;
    this.playBtn.setAttribute('aria-label', this.playing ? 'Pause' : 'Play');
    this.el.classList.toggle('is-playing', this.playing);
    this.paintRuler();
    this.rulerEl.setAttribute('aria-valuenow', String(Math.round(this.t / d.duration * 100)));
    this.rulerEl.setAttribute('aria-valuetext', this.whenEl.textContent);
  };

  Play.prototype.paint = function (shot) {
    var ctx = fitCanvas(this.overlay, this.W);
    if (!ctx || !this.data) return;
    // The daylight of the hour, over the street map and under the line.
    var sky = this.shown.sky;
    if (sky && sky[3] > 0.002) {
      ctx.fillStyle = 'rgba(' + Math.round(sky[0]) + ',' + Math.round(sky[1]) + ',' + Math.round(sky[2]) + ',' + sky[3] + ')';
      ctx.fillRect(0, 0, this.W, this.H);
    }
    this.lineArt.paint(ctx, this.W, this.H, shot, this.shown.p, this.shown.dims);
  };

  /* The replay's timeline, as the app's _RulerPainter draws it: a tick for
     each photo that comes up, in its day's color; the hurried stretches
     dotted; the days named underneath; the playhead in the accent. */
  Play.prototype.paintRuler = function () {
    var canvas = this.rulerEl, d = this.data, ruler = d.ruler, width = d.layout.ruler[2];
    var ctx = fitCanvas(canvas, width);
    if (!ctx) return;
    function x(s) { return s / ruler.duration * width; }
    var track = 14, i;
    for (i = 0; i < ruler.stretches.length; i++) {
      var st = ruler.stretches[i];
      if (st[2]) {
        ctx.fillStyle = FAINT;
        for (var dx = x(st[0]) + 1; dx < x(st[1]) - 1; dx += 4) {
          ctx.beginPath();
          ctx.arc(dx, track, 1, 0, 2 * Math.PI);
          ctx.fill();
        }
      } else {
        ctx.strokeStyle = HAIRLINE;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x(st[0]), track);
        ctx.lineTo(x(st[1]), track);
        ctx.stroke();
      }
    }
    for (i = 0; i < ruler.ticks.length; i++) {
      ctx.fillStyle = ruler.ticks[i][1];
      ctx.fillRect(x(ruler.ticks[i][0]), track - 4, 1.5, 8);
    }
    ctx.fillStyle = ACCENT;
    ctx.fillRect(x(this.t) - 1, track - 7, 2, 14);
    ctx.fillStyle = MUTED;
    ctx.font = '500 10px "Work Sans", system-ui, sans-serif';
    ctx.textBaseline = 'top';
    var spacing = 1.4, free = -Infinity;
    for (i = 0; i < ruler.days.length; i++) {
      var left = x(ruler.days[i][0]);
      if (left < free) continue;
      var label = ruler.days[i][1], lw = 0, c;
      for (c = 0; c < label.length; c++) lw += ctx.measureText(label[c]).width + spacing;
      if (left + lw > width) break;
      var at = left;
      for (c = 0; c < label.length; c++) {
        ctx.fillText(label[c], at, track + 12);
        at += ctx.measureText(label[c]).width + spacing;
      }
      free = left + lw + 8;
    }
  };

  Play.prototype.state = function () {
    return this.data && { t: this.t, playing: this.playing, waiting: this.waiting, frame: this.frameAt(this.t).frame, map: !!(this.map && this.map.ready) };
  };

  Play.prototype.step = function (dt) {
    this.manual = true;
    if (this.playing) {
      this.t = Math.min(this.data.duration, this.t + dt);
      if (this.t >= this.data.duration) this.playing = false;
    }
    this.render();
  };

  /* ------------------------------------------------------------ the phone */

  function mount(root) {
    if (root.__krDemo) return root.__krDemo;
    var pageEl = root.querySelector('.kr-pg[data-page]');
    var playEl = root.querySelector('.kr-rp[data-play]');
    if (!pageEl || !playEl) return null;
    var demo = {
      root: root,
      token: root.getAttribute('data-token') || '',
      style: root.getAttribute('data-style') || '',
      fine: !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches),
      dead: false,
      filmOpen: false,
      held: false
    };
    var page = demo.page = new Page(pageEl, demo);
    var play = demo.play = new Play(playEl, demo);

    demo.openPlay = function (photo) {
      if (demo.filmOpen) return;
      demo.filmOpen = true;
      root.classList.add('is-film');
      playEl.inert = false;
      pageEl.inert = true;
      play.open(photo);
      root.dispatchEvent(event('kr-play-open', { photo: photo }));
      // The play button has the keyboard once its screen is up.
      requestAnimationFrame(function () {
        try { play.playBtn.focus({ preventScroll: true }); } catch (e) { /* no focus */ }
      });
    };
    // Held (by the How it works stage while its story plays): the list
    // scrolls only as it is driven, a wheel or a thumb over the phone goes on
    // scrolling the page, and the hint waits. Let go, it is the visitor's:
    // with a mouse straight away, on a touch screen after a tap.
    demo.hold = function (on) {
      on = !!on;
      if (demo.held === on) return;
      demo.held = on;
      root.classList.toggle('is-held', on);
      root.inert = on;
      if (on) {
        if (page.live) {
          page.live = false;
          pageEl.classList.remove('is-live');
        }
      } else {
        page.release();
        if (demo.fine) page.goLive();
        page.dirty = true;
        page.kick();
      }
    };
    demo.closePlay = function () {
      if (!demo.filmOpen) return;
      demo.filmOpen = false;
      var photo = play.close();
      root.classList.remove('is-film');
      playEl.inert = true;
      pageEl.inert = false;
      page.back(photo);
      root.dispatchEvent(event('kr-play-close', { photo: photo }));
    };
    // Escape closes Play, as the app's back does, unless it's someone typing
    // somewhere.
    function onKey(e) {
      if (e.key !== 'Escape' || !demo.filmOpen) return;
      var at = document.activeElement;
      if (at && !root.contains(at) && (/^(INPUT|TEXTAREA|SELECT)$/.test(at.tagName) || at.isContentEditable)) return;
      demo.closePlay();
      e.preventDefault();
    }
    document.addEventListener('keydown', onKey);

    // The maps load once the phone is near; out of sight, Play pauses and the
    // list (on a touch screen) waits for a tap again.
    var near = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
      if (entries[entries.length - 1].isIntersecting) page.wake();
    }, { rootMargin: '600px 0px' }) : null;
    if (near) near.observe(root);
    var seen = typeof IntersectionObserver === 'function' ? new IntersectionObserver(function (entries) {
      var e = entries[entries.length - 1];
      if (!e.isIntersecting) {
        if (play.playing) play.pause();
        page.sleep();
      } else {
        page.dirty = true;
        page.kick();
      }
    }) : null;
    if (seen) seen.observe(root);
    var resize = typeof ResizeObserver === 'function' ? new ResizeObserver(function () {
      if (page.map) page.map.fit();
      if (play.map) play.map.fit();
      page.dirty = true;
      page.kick();
      play.render();
    }) : null;
    if (resize) resize.observe(root);

    page.load().then(function () {
      if (!near) page.wake();
      root.classList.add('is-ready');
    }).catch(function (err) {
      root.classList.add('is-broken');
      if (window.console) console.warn('kr-journeys-demo:', err);
    });

    root.__krDemo = {
      page: page,
      play: play,
      open: demo.openPlay,
      close: demo.closePlay,
      hold: demo.hold,
      drive: function (s) { page.drive(s); },
      wake: function () { page.wake(); },
      fit: function () {
        if (page.map) page.map.fit();
        if (play.map) play.map.fit();
        page.dirty = true;
        page.kick();
      },
      destroy: function () {
        demo.dead = true;
        document.removeEventListener('keydown', onKey);
        if (near) near.disconnect();
        if (seen) seen.disconnect();
        if (resize) resize.disconnect();
        if (page.io) page.io.disconnect();
        if (page.map) page.map.remove();
        if (play.map) play.map.remove();
        delete root.__krDemo;
      }
    };
    if (root.hasAttribute('data-held')) demo.hold(true);
    root.dispatchEvent(event('kr-demo-mounted', {}));
    return root.__krDemo;
  }

  function mountAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-kr-demo]');
    for (var i = 0; i < roots.length; i++) mount(roots[i]);
  }

  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-kr-demo]');
    for (var i = 0; i < roots.length; i++) if (roots[i].__krDemo) roots[i].__krDemo.destroy();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mountAll(); });
  else mountAll();
})();
